import { useAtomValue } from 'jotai'
import { useEffect, useRef, useState } from 'react'
import { canvasRootElAtom } from '../atoms'
import { useCanvasStore } from '../canvas-store'

/**
 * Resolves a layer's DOM element by its `data-layer-id` attribute,
 * scoped to the current canvas root (safe with multiple canvases on one page).
 *
 * Re-queries on every `deps` change (e.g. when `layer` atom updates) and
 * detects stale/detached elements via `isConnected`, returning null if the
 * node has been removed from the document.
 */
export function useLayerElement(layerId: string | null, deps: unknown[] = []) {
	const store = useCanvasStore()
	const canvasRoot = useAtomValue(canvasRootElAtom, { store })
	const [el, setEl] = useState<HTMLElement | null>(null)
	const prevEl = useRef<HTMLElement | null>(null)

	useEffect(() => {
		if (!layerId || !canvasRoot) {
			prevEl.current = null
			setEl(null)
			return
		}

		const found = canvasRoot.querySelector<HTMLElement>(`[data-layer-id="${CSS.escape(layerId)}"]`)

		// Only update state if the element actually changed (avoids unnecessary renders)
		if (found !== prevEl.current) {
			prevEl.current = found
			setEl(found)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [layerId, canvasRoot, ...deps])

	// Detect detached elements — return null if the node is no longer in the document
	if (el && !el.isConnected) {
		return null
	}

	return el
}
